/* ==========================================================================
   FLC Forge — Predictive Search
   ========================================================================== */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input        = this.querySelector('[data-search-input]');
    this.clearBtn     = this.querySelector('[data-search-clear]');
    this.panel        = this.querySelector('[data-search-panel]');
    this.leftLabel    = this.querySelector('[data-search-left-label]');
    this.leftContent  = this.querySelector('[data-search-left-content]');
    this.rightLabel   = this.querySelector('[data-search-right-label]');
    this.resultsGrid  = this.querySelector('[data-search-results]');
    this.viewAllLink  = this.querySelector('[data-search-view-all]');
    this.viewAllLabel = this.querySelector('[data-search-view-all-label]');
    this.viewAllCount = this.querySelector('[data-search-view-all-count]');
    this.carouselNav  = this.querySelector('[data-carousel-nav]');
    this.prevBtn      = this.querySelector('[data-carousel-prev]');
    this.nextBtn      = this.querySelector('[data-carousel-next]');
    this.searchBar    = this.closest('.header-search');
    this.abort        = null;

    // Save Liquid-rendered default state
    this.defaultLeftLabel   = this.leftLabel?.textContent  || '';
    this.defaultLeftHTML    = this.leftContent?.innerHTML  || '';
    this.defaultRightLabel  = this.rightLabel?.textContent || '';
    this.defaultResultsHTML = this.resultsGrid?.innerHTML  || '';

    this.hasDefaultContent = !!(
      this.defaultLeftHTML.trim() || this.defaultResultsHTML.trim()
    );
    this.collectionCount = this.dataset.collectionCount || '';
    this.collectionUrl   = this.dataset.collectionUrl   || '';

    this.input?.addEventListener('input', FLC.debounce(() => this.handleInput(), 300));
    this.input?.addEventListener('focus', () => {
      if (this.input?.value.trim()) {
        this.openPanel();
      } else if (this.hasDefaultContent) {
        this.restoreDefaults();
        this.openPanel();
      }
    });
    this.clearBtn?.addEventListener('click', () => this.clearSearch());
    this.prevBtn?.addEventListener('click', () => this.scrollCarousel(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollCarousel(1));
    this.resultsGrid?.addEventListener('scroll', () => this.updateCarouselNav(), { passive: true });

    // Close panel when SearchToggle removes is-open
    if (this.searchBar) {
      new MutationObserver(() => {
        if (!this.searchBar.classList.contains('is-open')) this.closePanel();
      }).observe(this.searchBar, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('click', (e) => {
      if (!this.searchBar?.contains(e.target)) this.closePanel();
    });
  }

  openPanel() {
    this.panel?.removeAttribute('hidden');
    requestAnimationFrame(() => this.updateCarouselNav());
  }

  closePanel() {
    this.panel?.setAttribute('hidden', '');
  }

  clearSearch() {
    if (this.input) this.input.value = '';
    this.clearBtn?.classList.remove('is-visible');
    this.restoreDefaults();
    if (this.hasDefaultContent) {
      this.openPanel();
    } else {
      this.closePanel();
    }
    this.input?.focus();
  }

  restoreDefaults() {
    if (this.leftLabel)   this.leftLabel.textContent   = this.defaultLeftLabel;
    if (this.leftContent) this.leftContent.innerHTML   = this.defaultLeftHTML;
    if (this.rightLabel)  this.rightLabel.textContent  = this.defaultRightLabel;
    if (this.resultsGrid) this.resultsGrid.innerHTML   = this.defaultResultsHTML;
    this.panel?.removeAttribute('data-has-query');
    this.abort?.abort();

    this.carouselNav?.removeAttribute('hidden');

    if (this.collectionCount && this.collectionUrl) {
      if (this.viewAllLink)  { this.viewAllLink.href = this.collectionUrl; this.viewAllLink.removeAttribute('hidden'); }
      if (this.viewAllLabel) this.viewAllLabel.textContent = 'Show All';
      if (this.viewAllCount) this.viewAllCount.textContent = `(${this.collectionCount})`;
    } else {
      this.viewAllLink?.setAttribute('hidden', '');
    }

    if (this.resultsGrid) this.resultsGrid.scrollLeft = 0;
    this.updateCarouselNav();
  }

  async handleInput() {
    const query = this.input?.value.trim();

    this.clearBtn?.classList.toggle('is-visible', !!query);

    if (!query) {
      this.restoreDefaults();
      if (this.hasDefaultContent) {
        this.openPanel();
      } else {
        this.closePanel();
      }
      return;
    }

    this.openPanel();
    this.panel?.setAttribute('data-has-query', '');
    this.setLoading();
    this.abort?.abort();
    this.abort = new AbortController();

    try {
      const [suggestRes, sectionRes] = await Promise.all([
        fetch(
          `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product,page,article,collection&resources[limit]=6`,
          { signal: this.abort.signal, headers: { Accept: 'application/json' } }
        ),
        fetch(
          `/search?q=${encodeURIComponent(query)}&type=product&sections=search-product-grid`,
          { signal: this.abort.signal }
        )
      ]);

      // Suggestions
      if (suggestRes.ok) {
        const suggestData = await suggestRes.json();
        const r = suggestData.resources?.results || {};
        this.renderSuggestions({
          products:    r.products    || [],
          collections: r.collections || [],
          pages:       r.pages       || [],
          articles:    r.articles    || [],
        }, query);
      }

      // Product cards — server-rendered via section rendering
      if (sectionRes.ok) {
        const sectionData = await sectionRes.json();
        const html  = sectionData['search-product-grid'] || '';
        const doc   = new DOMParser().parseFromString(html, 'text/html');
        const cards = doc.querySelector('[data-search-cards]');

        if (this.rightLabel) this.rightLabel.textContent = 'Products';

        if (cards && cards.children.length) {
          if (this.resultsGrid) this.resultsGrid.innerHTML = cards.innerHTML;
          const total = cards.dataset.searchTotal || '';
          if (this.viewAllLink)  { this.viewAllLink.href = `${this.dataset.resultsUrl}?q=${encodeURIComponent(query)}&type=${this.dataset.searchType || 'product'}`; this.viewAllLink.removeAttribute('hidden'); }
          if (this.viewAllLabel) this.viewAllLabel.textContent = 'Show All';
          if (this.viewAllCount) this.viewAllCount.textContent = total ? `(${total})` : '';
          this.carouselNav?.removeAttribute('hidden');
        } else {
          if (this.resultsGrid) this.resultsGrid.innerHTML = `<p class="search-panel__no-results">No results found for "<em>${this.esc(query)}</em>". Please try another search.</p>`;
          this.viewAllLink?.setAttribute('hidden', '');
          this.carouselNav?.setAttribute('hidden', '');
        }
        this.updateCarouselNav();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Predictive search error:', err);
        this.restoreDefaults();
      }
    }
  }

  setLoading() {
    if (this.resultsGrid) {
      this.resultsGrid.scrollLeft = 0;
      this.resultsGrid.innerHTML = '<div class="search-panel__loading" role="status" aria-label="Searching…"></div>';
    }
  }

  scrollCarousel(dir) {
    if (!this.resultsGrid) return;
    const card = this.resultsGrid.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.resultsGrid).columnGap) || 0;
    this.resultsGrid.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  updateCarouselNav() {
    requestAnimationFrame(() => {
      if (!this.prevBtn || !this.nextBtn || !this.resultsGrid) return;
      this.prevBtn.disabled = this.resultsGrid.scrollLeft <= 1;
      this.nextBtn.disabled =
        this.resultsGrid.scrollLeft + this.resultsGrid.clientWidth >= this.resultsGrid.scrollWidth - 1;
    });
  }

  renderSuggestions({ products, collections, pages, articles }, query) {
    if (this.leftLabel) this.leftLabel.textContent = 'Search Suggestions';
    if (!this.leftContent) return;

    const groups = [
      { label: 'Products',    items: products },
      { label: 'Collections', items: collections },
      { label: 'Pages',       items: pages },
      { label: 'Blog Posts',  items: articles },
    ].filter(g => g.items.length);

    if (!groups.length) {
      this.leftContent.innerHTML = '';
      return;
    }

    this.leftContent.innerHTML = groups.map(g =>
      `<li class="search-panel__group">
        <p class="search-panel__group-label">${g.label}</p>
        <ul class="search-panel__group-list">${g.items.map(item => {
          const label = item.styled_title || this.esc(item.title);
          return `<li><a href="${item.url}" class="search-panel__list-link">${label}</a></li>`;
        }).join('')}</ul>
      </li>`
    ).join('');
  }

  esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
